import { Object3D, Mesh, AnimationMixer } from 'three';
import { VoiceStreaming } from './lib/voiceStreaming';
import { TextPlaneButton } from '@/_models/virtualStaging/AIAvater/TextPlane';

interface AIAvatarOptions {
  lang?: string;
  gender?: 'male' | 'female';
}

const DEFAULT: AIAvatarOptions = {
  lang: 'en',
  gender: 'male',
};

export type AIAvatarStatus = 'connecting' | 'listening' | 'answering' | 'thinking';

export class AIAvatar {
  // Start Avatar Model Block
  private avatar: Object3D;

  private meshes: Mesh[] = [];

  private clipActions: any; // AnimationMixer[];
  // End Avatar Model Block

  // Start Mouth animation
  private mouthOpenValue = 0;

  private readonly minOpenValue = 0.05;

  private lastMouthUpdate = 0;

  private readonly mouthUpdateInterval = 40;

  private animationFrameId: number | null = null;
  // End Mouth Animation

  private isSpeaking = false;

  private options: AIAvatarOptions;

  private voiceStreaming: VoiceStreaming;

  private statusChangeCallbacks: Function[] = [];

  private startChatCallbacks: Function[] = [];

  private status: AIAvatarStatus;

  private startButtonComponent: TextPlaneButton;

  constructor(avatarVsObject, dependencies: any, options?: AIAvatarOptions) {
    const avatarNode = avatarVsObject.node;
    this.avatar = avatarNode.model;
    this.options = { ...DEFAULT, ...options };
    this.clipActions = this.transformClipActions(avatarNode.clipActions);
    this.findMouthMeshes();
    this.initAvatarAnimations();
    this.initVoiceStreaming();
    this.initStartButton(avatarVsObject, dependencies.showcase);
  }

  private initVoiceStreaming() {
    this.voiceStreaming = new VoiceStreaming({
      handleTranscriptChanges: (history, content) => {
        if (!content) return;
        if (content.role === 'USER') {
          // User questing was transcribed, the next answers from Assistance
          this.emitStatusChange('thinking');
        }
        console.log(`Message from ${content.role}:`, content.message);
      },
      handleAudioResponseStarted: () => {
        this.mouthOpenValue = 0.3;
        console.log('Avatar Response Playback Started!');
        this.isSpeaking = true;
        this.startAnimation();
        this.emitStatusChange('answering');
      },
      handleAudioResponseFinished: () => {
        console.log('Avatar Response Playback Finished!');
        this.isSpeaking = false;
        this.stopAnimation();
        this.emitStatusChange('listening');
      },
    });
  }

  private async initStartButton(vsObject, showcase) {
    try {
      if (!showcase || !vsObject.isAIAvatar) return;

      // Create TextPlane button for this AI Avatar
      const AIAvatarStartButton = new TextPlaneButton(showcase, vsObject);
      await AIAvatarStartButton.init();

      // Override the click handler to show VoiceStatus
      if (AIAvatarStartButton?.component) {
        AIAvatarStartButton.component.spyOnEvent({
          eventType: 'textPlaneClicked',
          onEvent: () => {
            this.handleTextPlaneClicked();
          },
        });
      }
      this.startButtonComponent = AIAvatarStartButton;
    } catch (error) {
      console.error(`Failed to create TextPlane button for AI Avatar ${vsObject.id}:`, error);
    }
  }

  public handleTextPlaneClicked () {
    this.startButtonComponent.hide();
    this.startChatCallbacks.forEach((callback) => {
      callback();
    });
    this.emitStatusChange('connecting');
    setTimeout(() => {
      this.start();
    }, 500);
  }

  public start() {
    this.voiceStreaming.startStreaming();
    this.emitStatusChange('listening');
  }

  public stop() {
    this.voiceStreaming.stopStreaming();
    this.stopAnimation();
    this.emitStatusChange('connecting');
  }

  public onStatusChange(callback) {
    this.statusChangeCallbacks.push(callback);
  }

  public onStartChat(callback) {
    this.startChatCallbacks.push(callback);
  }

  public getStatus() {
    return this.status;
  }

  public showStartButton() {
    this.startButtonComponent?.show();
  }

  public hideStartButton() {
    this.startButtonComponent?.hide();
  }

  public async setVisibleStartButton(isVisible: boolean) {
    if (isVisible) {
      this.showStartButton();
    } else {
      this.hideStartButton();
    }
  }

  /**
   * Clean up all resources and stop all activities
   */
  public destroy() {
    try {
      if (this.voiceStreaming) {
        this.voiceStreaming.stopStreaming();
      }

      this.stopAnimation();

      if (this.startButtonComponent) {
        this.startButtonComponent.destroy();
        this.startButtonComponent = null;
      }

      this.statusChangeCallbacks = [];
      this.startChatCallbacks = [];

      this.isSpeaking = false;
      this.mouthOpenValue = 0;
      this.meshes = [];
    } catch (error) {
      console.error('Error during AIAvatar cleanup:', error);
    }
  }

  private emitStatusChange(status: AIAvatarStatus) {
    this.status = status;
    switch (status) {
      case 'listening':
      case 'thinking':
        this.clipActions?.answering && this.clipActions.answering.reset().stop();
        this.clipActions?.thinking && this.clipActions.thinking.reset().play();
        break;
      case 'answering':
        this.clipActions?.thinking && this.clipActions.thinking.reset().stop();
        this.clipActions?.answering && this.clipActions.answering.reset().play();
        break;
      case 'connecting':
      default:
        this.clipActions?.thinking && this.clipActions.thinking.reset().stop();
        this.clipActions?.answering && this.clipActions.answering.reset().stop();
        break;
    }
    this.statusChangeCallbacks.forEach((callback) => {
      callback(status);
    });
  }

  private initAvatarAnimations() {
    if (!this.clipActions) return;
    this.clipActions?.idle && this.clipActions.idle.play();
    this.clipActions?.thinking && this.clipActions.thinking.stop();
    this.clipActions?.answering && this.clipActions.answering.stop();
  }

  private transformClipActions(clipActions) {
    return clipActions.reduce((acc, action) => {
      const animationName = action._clip.name.toLowerCase();
      switch (animationName) {
        case 'listening': // support animation for old model
        case 'thinking':
          acc.thinking = action;
          break;
        case 'chatting': // support animation for old model
        case 'agreeing': // support animation for old model
        case 'answering':
          acc.answering = action;
          break;
        default:
          acc[animationName] = action;
          break;
      }
      return acc;
    }, {});
  }

  /**
   * Find meshes with 'mouthOpen' or 'jawOpen' morph targets in the avatar
   */
  private findMouthMeshes(): void {
    this.meshes = [];
    this.avatar.traverse((obj) => {
      // @ts-ignore
      if ((obj as Mesh).isMesh && obj.morphTargetDictionary && obj.morphTargetInfluences) {
        const mesh = obj as Mesh;
        const dict = mesh.morphTargetDictionary;
        if ('mouthOpen' in dict || 'jawOpen' in dict) {
          this.meshes.push(mesh);
        }
      }
    });

    if (!this.meshes.length) {
      console.warn('AIAvatar: No mouth morph targets found.');
    }
  }

  /**
   * Start recursive animation loop
   */
  private startAnimation(): void {
    if (this.animationFrameId === null) {
      this.animate();
    }
  }

  /**
   * Stop animation and reset mouth morphs
   */
  private stopAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.mouthOpenValue = 0;
    this.applyMouthInfluence();
  }

  /**
   * Animation frame callback
   */
  private animate = (): void => {
    this.animationFrameId = requestAnimationFrame(this.animate);
    this.updateMouth();
  };

  /**
   * Gradually close mouth between word boundaries
   */
  private updateMouth(): void {
    if (!this.isSpeaking) return;

    const now = performance.now();
    // Only update mouth position at specified intervals
    if (now - this.lastMouthUpdate > this.mouthUpdateInterval) {
      if (Math.random() > 0.5) {
        this.mouthOpenValue = this.minOpenValue + Math.random() * 0.25;
      } else {
        this.mouthOpenValue = Math.max(this.minOpenValue, this.mouthOpenValue - 0.02);
      }
      this.lastMouthUpdate = now;
    }
    this.applyMouthInfluence();
  }

  /**
   * Apply current mouthOpenValue to all morph targets
   */
  private applyMouthInfluence(): void {
    for (const mesh of this.meshes) {
      const dict = mesh.morphTargetDictionary;
      if ('mouthOpen' in dict) {
        mesh.morphTargetInfluences![dict.mouthOpen] = this.mouthOpenValue;
      }
      if ('jawOpen' in dict) {
        mesh.morphTargetInfluences![dict.jawOpen] = this.mouthOpenValue;
      }
    }
  }
}
